import { useEffect, lazy, Suspense } from 'react'
import styled from 'styled-components'
import { Grid, P, Wrapper } from '@farewill/ui'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { Switch, useLocation } from 'react-router-dom'

import {
  fetchProbateCase,
  fetchProbateCaseEvents,
  fetchLead,
  unsetProduct,
} from 'state/actions'
import { PRODUCTS } from 'utils/enums'

import Header from 'components/product-header'
import NavigationTabs from 'components/navigation-tabs'
import PrivateRoute from 'components/private-route/containers'
import NotFoundMessage from 'components/not-found-message'
import {
  TasksProvider,
  TaskStagesProvider,
} from 'components/tasks-panel/context'
import FWLSBanner from 'components/fwls-banner'

import CaseDetails from './summary'
import Tasks from './tasks'
import Documents from './documents'
import Errors from './errors'
import Activity from 'components/activity'
import Quote from './quote'
import EstateQuestionnaire from './estate-questionnaire'
import { GTR } from '@farewill/ui/tokens'
import ENV from 'config/environment'

import { getNavLinks, getProbateEstateId } from './helpers'

const Forms = lazy(() => import('./forms'))

const StyledWrapper = styled(Wrapper)`
  position: relative;
`

const ProbateCase = ({ match }) => {
  const dispatch = useDispatch()
  const { lead, probateCase } = useSelector(
    (state) => ({
      lead: state.lead.attributes && state.lead,
      probateCase: state.probateCase.attributes && state.probateCase,
    }),
    shallowEqual
  )

  const probateCaseId = match.params.id
  const leadId = probateCase?.attributes.leadId
  const probateEstateId = getProbateEstateId(probateCase)
  const showEstateQuestionnaire = ENV.FF_PROBATE_ESTATE_QUESTIONNAIRE_ENABLED

  useEffect(() => {
    dispatch(fetchProbateCase(probateCaseId))
  }, [dispatch, probateCaseId])

  useEffect(() => {
    dispatch(fetchProbateCaseEvents(probateCaseId))
  }, [dispatch, probateCaseId])

  useEffect(() => {
    if (leadId) dispatch(fetchLead(leadId))
  }, [dispatch, leadId])

  useEffect(() => {
    return () => dispatch(unsetProduct('probate'))
  }, [dispatch])

  const location = useLocation()

  if (!probateCase || !lead) return <P size="L">Loading...</P>

  const isTasksRoute = location.pathname.endsWith('/tasks')
  const isEstateRoute = location.pathname.endsWith('/estate')

  return (
    <StyledWrapper data-sticky-content="probate-case" padding={[GTR.XL, 0, 0]}>
      <Header
        attributes={probateCase.attributes}
        productType="probate"
        caseId={probateCase.id}
        showLastActivityAt={false}
        showLastCallAt={false}
      />
      <Wrapper separator>
        <NavigationTabs links={getNavLinks(probateCase)} />
        <FWLSBanner
          referredToFWLS={
            probateCase.attributes.referredTo === 'farewill_legal_services'
          }
        />
      </Wrapper>
      <Grid separator>
        <Grid.Item span={12} spanFromL={isTasksRoute || isEstateRoute ? 12 : 8}>
          <Suspense fallback={<div>Loading...</div>}>
            <Switch>
              <PrivateRoute path={match.path} component={CaseDetails} exact />
              <PrivateRoute path={`${match.path}/tasks`} component={Tasks} />
              <PrivateRoute path={`${match.path}/errors`} component={Errors} />
              <PrivateRoute path={`${match.path}/forms`} component={Forms} />
              <PrivateRoute
                path={`${match.path}/documents`}
                component={Documents}
              />
              <PrivateRoute
                path={`${match.path}/history`}
                component={Activity}
                leadId={leadId}
                product="probate"
                showEmailFilter
                showCallsFilter
                showPaymentsFilter
                showOthersFilter
                tasksRoutePath={`${match.url}/tasks`}
              />
              <PrivateRoute path={`${match.path}/quote`} component={Quote} />
              {showEstateQuestionnaire && (
                <PrivateRoute
                  path={`${match.path}/estate`}
                  component={EstateQuestionnaire}
                  probateEstateId={probateEstateId}
                  probateCase={probateCase}
                />
              )}
              <PrivateRoute component={NotFoundMessage} />
            </Switch>
          </Suspense>
        </Grid.Item>
      </Grid>
    </StyledWrapper>
  )
}

const ProbateCaseWithProvider = (props) => (
  <TaskStagesProvider probateCaseId={props.match.params.id}>
    <TasksProvider product={PRODUCTS.PROBATE}>
      <ProbateCase {...props} />
    </TasksProvider>
  </TaskStagesProvider>
)

export default ProbateCaseWithProvider
